// THE INFECTED — the brain, shared by the client and the server.
//
// This file is deliberately DEPENDENCY-FREE: no Babylon, no DOM, no fs, no
// imports at all. It is the one copy of the infected AI, imported by
//
//   • src/world/zombies.js  — the browser renderer (and the ?solo=1 host)
//   • server/zombies.mjs    — the authoritative host on the multiplayer server
//
// Extracted verbatim from src/world/zombies.js when the server port landed
// (docs/INFECTED.md Z1, Z5, §12.5). It is .mjs on purpose: the repo's
// package.json has no `"type": "module"`, so a .js file here would be loaded
// as CommonJS by Node and the server could not import it. esbuild resolves the
// explicit .mjs specifier without any config change.
//
// The sim talks to its host ONLY through the `env` adapter it is handed:
//
//   env: {
//     towns: parsed towns.json,
//     biomeAt(x, z) -> int,          // 0 = water, 4..6 = forest, 9 = alpine
//     blocked(x, z) -> bool,         // a wall at chest height (doors are gaps)
//     players() -> [{ id, x, z, dead, n, v }],   // n = noise radius, v = visibility
//     hit(ev),                       // an infected landed a swing on a player
//
//     // OPTIONAL, both added for occlusion (see _lineBlocked below). Every
//     // one of them has a safe fallback, so a host that has not been updated
//     // yet keeps working with exactly its current behaviour:
//     segmentBlocked(ax, az, bx, bz, y0, y1) -> bool,  // src/world/query.mjs
//     groundY(x, z) -> metres,       // terrain height, for the y band
//   }
// ---------------------------------------------------------------------------
// Section 9 of the spec: ONE source of truth for every tuning number, so the
// sim, the renderer and the tests can never drift apart.
export const INF = {
  hp: 80, headMul: 3.0, limbMul: 0.7, legCripple: 60,
  speed: { wander: 1.0, alert: 2.2, chase: 5.6, crawl: 1.2 }, turn: 3.0,
  sightBase: 60, sightCone: Math.PI / 4, nearBubble: 8, forestMul: 0.7,
  awake: {
    alert: 0.35, chase: 1.0, calm: 0.15, decay: 0.25,
    sight: 1.6, hear: 1.2, ping: 0.5, call: 0.6, max: 1.5,
  },
  atk: {
    range: 2.0, breakRange: 2.6, crawlRange: 1.4,
    windup: 0.45, swingGap: 0.9, recover: 2.4,
    dmg: [6, 11], bleed: 0.35, infect: 0.06, blockMul: 0.3,
  },
  stagger: {
    heavy: 0.70, light: 0.45, lightChance: 0.5, gunChance: 0.35,
    minInterval: 0.30, knockdownDmg: 30, knockdownChance: 0.25, knockdown: 2.2,
  },
  chase: { loseDist: 60, loseTime: 6, maxTime: 45, leash: 400, capPerTarget: 12 },
  call: { radius: 30, max: 6, cooldown: 8 },
  stream: {
    activate: 200, deactivate: 320, minSpawn: 60, snapshot: 220,
    capPerPlayer: 40, capGlobal: 150,
  },
  respawn: { corpse: 120, cooldown: 300, jitter: 60, playerBlock: 150 },
  // Occlusion. Every number here is a y interval above the GROUND at the two
  // ends of the line, in metres, because the sim itself is 2D and the world
  // query wants an absolute band (see _lineBlocked / _band).
  occl: {
    // NOT YET LIVE. These five bands are only read on the env.segmentBlocked
    // path, and NEITHER host installs that method: the browser's env is built
    // in src/world/zombies.js as { towns, biomeAt, blocked, players, hit } and
    // the server's in server/zombies.mjs as the same five. Neither supplies
    // groundY either, so _band() would answer [undefined, undefined] even if
    // segmentBlocked appeared. Until both hosts are wired IN THE SAME CHANGE
    // (doing one host alone makes ?solo=1 and online disagree about who can
    // see whom), a crawler sees and reaches exactly as far as a walker and
    // every line resolves to the chest-band point walk below. They are kept
    // here, and threaded through los() / _canReach(), so that wiring the hosts
    // is a two-line change rather than a re-derivation — but do not read them
    // as shipped behaviour. See the handoff note.
    eye: [0.9, 1.7],        // a walker's eyes
    crawlEye: [0.2, 0.6],   // a crawler's, dragging itself along the floor
    swing: [0.4, 1.7],      // the arc of a claw swing: chest to head
    crawlSwing: [0.1, 0.8], // a crawler can only reach your legs
    body: [0.35, 1.5],      // the band env.blocked already bakes in
    // THE SHIPPED PATH: a point walk at fixed SPACING, never a fixed count.
    // 0.5 m is under the thinnest wall band either host publishes (0.6 m on
    // the server's Obstacles, ~0.5 m in buildings.blockedBand), so a sample
    // cannot step across a wall.
    //
    // maxSamples is a COST GUARD, not a resolution setting, and it has to sit
    // above the longest line anyone asks for or it silently becomes one. At
    // 128 it did: _sense calls los() out to INF.sightBase (60) * p.v, and
    // senseOf clamps v to 2 — which a standing sprinting player with a torch
    // at night reaches — so a 120 m line was sampled every 0.94 m, 1.56x the
    // 0.6 m wall band. Measured against a 5 cm walk of the same env.blocked
    // over the real towns.json, 40 000 lines per setting in its densest
    // settlement: of the sight lines that are GENUINELY blocked, the 128-cap
    // walk reported 0.63 % clear at v 1.0, 0.86 % at v 1.5 and 3.48 % at v 2.0
    // — the case that matters most, a lit sprinting player at night, was the
    // case the horde could see through walls. At 272 it is 0.62 % flat across
    // all three, i.e. the 0.5 m spacing's own residual with the cap no longer
    // contributing. _sense's `d > 130` guard is the real ceiling, so
    // 272 = ceil(130 / 0.48) makes the invariant unconditional (measured worst
    // spacing 0.500 m for every d <= 130, against 1.016 m at 128), and the
    // walk still exits on the first blocked sample.
    step: 0.5, maxSamples: 272,
  },
  tick: 0.2, // 5 Hz (Z3)
};

// snapshot enum order is load-bearing — the renderer mirrors by index (Z31)
export const ST = {
  idle: 0, wander: 1, alerted: 2, chase: 3, attack: 4, stagger: 5, crawl: 6, dead: 7,
};
export const ST_NAMES = ['idle', 'wander', 'alerted', 'chase', 'attack', 'stagger', 'crawl', 'dead'];

// one-shot events carried on the snapshot row so the renderer can fire clips
// and voices without simulating anything itself
export const EV = { none: 0, swing: 1, scream: 2, hurt: 3, death: 4, stagger: 5 };

// building loot tags → how many infected that building is worth (Z9)
const TAG_W = {
  military: 2.5, police: 2.0, emergency: 2.0, medical: 1.6,
  industrial: 1.2, fuel: 1.2, vehicle: 1.2,
  civic: 1.0, school: 1.0, office: 1.0, commercial: 1.0, retail: 1.0, food: 1.0,
  residential: 0.9, rural: 0.5, infrastructure: 0.4, civilian: 0.0,
};

// the exact hash wolves.mjs / animals.js already use — same numbers on both
// sides of the wire, so home posts survive a restart (Z13)
export function hash2(x, z, salt) {
  let h = (x * 374761393 + z * 668265263 + salt * 971) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 100000) / 100000;
}

export function angWrap(a) {
  let d = a % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

// ===========================================================================
// THE BRAIN — no Babylon, no DOM. Move this file-for-file to server/zombies.mjs
// when the server sim lands; only `env` changes.
//
// env: {
//   towns: parsed towns.json,
//   biomeAt(x, z) -> int,            // 0 = water, 4..6 = forest
//   blocked(x, z) -> bool,           // a wall at chest height (doors are gaps)
//   players() -> [{ id, x, z, yaw, dead, n, v }],   // n = noise radius, v = visibility
//   hit(ev),                         // an infected landed a swing on a player
// }
// ===========================================================================
export class InfectedSim {
  constructor(env) {
    this.env = env;
    this.time = 0;
    this.slots = [];          // every planned infected on the map
    this.active = new Map();  // id -> entity
    this.plan();
  }

  // ---------------------------------------------------------------- planning
  plan() {
    const towns = this.env.towns;
    const arch = towns.archetypes;
    let planned = 0;
    towns.settlements.forEach((s, si) => {
      const bs = s.buildings;
      if (!bs?.length) return;
      let sum = 0;
      for (const b of bs) {
        const a = arch[b[0]];
        let w = 0;
        for (const tag of a.lootTags || []) w = Math.max(w, TAG_W[tag] ?? 0.6);
        sum += w;
      }
      const quota = Math.max(3, Math.min(90, Math.round(sum)));
      // The spec's `(i*7+3) % n` stride assumes 7 is coprime with the building
      // count. Primorsk has exactly 182 = 7×26 buildings, so a fixed 7 put all
      // 90 of its infected on the same 26 doorsteps and left the other 156
      // buildings empty. Pick the first prime that is actually coprime — same
      // determinism, same intent, no degenerate towns.
      let stride = 7;
      for (const p of [7, 11, 13, 17, 19, 23, 29]) {
        if (bs.length % p !== 0) { stride = p; break; }
      }
      for (let i = 0; i < quota; i++) {
        const b = bs[(i * stride + 3) % bs.length];
        const a = arch[b[0]];
        const [bw, bd] = a.footprint;
        const bx = b[1], bz = b[2], byaw = b[4] || 0;
        const h1 = hash2(si, i, 11), h2 = hash2(si, i, 12), h3 = hash2(si, i, 13);
        const cos = Math.cos(byaw), sin = Math.sin(byaw);
        let hx, hz, indoor = false;
        if (h1 < 0.4 && a.enterable && a.enterable !== 'none') {
          // 40 % stand INSIDE — looting a khrushchyovka has to be worse than
          // walking past it
          indoor = true;
          const lx = (h2 - 0.5) * bw * 0.7, lz = (h3 - 0.5) * bd * 0.7;
          hx = bx + lx * cos - lz * sin;
          hz = bz + lx * sin + lz * cos;
        } else {
          const ang = h2 * Math.PI * 2;
          const r = (Math.max(bw, bd) / 2) * (0.6 + h3 * 0.8);
          hx = bx + Math.sin(ang) * r;
          hz = bz + Math.cos(ang) * r;
        }
        this.slots.push({
          id: si * 1000 + i, si, slot: i, hx, hz, indoor,
          respawnT: 0, ent: null,
        });
        planned++;
      }
    });
    // wilderness wanderers: the road between two villages is never guaranteed
    // safe, but the wilderness is not where the horde lives (Z11)
    for (let iz = 0; iz < 10; iz++) {
      for (let ix = 0; ix < 10; ix++) {
        if (hash2(ix, iz, 21) > 0.12) continue;
        const hx = -5000 + (ix + hash2(ix, iz, 22)) * 1000;
        const hz = -5000 + (iz + hash2(ix, iz, 23)) * 1000;
        const b = this.env.biomeAt(hx, hz);
        if (b === 0 || b === 9) continue;
        this.slots.push({
          id: 90000 + iz * 10 + ix, si: -1, slot: iz * 10 + ix, hx, hz,
          indoor: false, respawnT: 0, ent: null,
        });
        planned++;
      }
    }
    this.planned = planned;
    return planned;
  }

  // --------------------------------------------------------------- streaming
  _spawn(slot) {
    const h = hash2(slot.si, slot.slot, 31);
    const e = {
      id: slot.id, slot, x: slot.hx, z: slot.hz,
      yaw: hash2(slot.si, slot.slot, 32) * Math.PI * 2,
      state: ST.idle, A: 0, target: null, lkpX: slot.hx, lkpZ: slot.hz,
      hp: INF.hp, legs: 0, crawler: false,
      t: 3 + h * 9, scanT: 0, chaseT: 0, loseT: 0,
      atkPhase: 2, atkT: 0, swings: 0, staggerT: 0,
      hitT: -99, callT: 0, corpseT: 0, stuck: 0,
      wx: slot.hx, wz: slot.hz, ev: EV.none, gait: h,
    };
    slot.ent = e;
    this.active.set(e.id, e);
    return e;
  }

  _despawn(e) {
    this.active.delete(e.id);
    if (e.slot) e.slot.ent = null;
  }

  // Streaming is the one loop that scales with PLAYERS × PLANNED SLOTS (709 of
  // them), so it is the hot spot on a busy server where the browser only ever
  // saw one or two players. Same decisions, same order of spawn, one distance
  // per (entity|slot, player) pair instead of three, and a cheap |dx|,|dz| box
  // rejects the ~99 % of slots that are nowhere near anybody before any
  // hypot() is paid for.
  _stream(players, dt) {
    const ACT = INF.stream.activate;
    const perPlayer = new Map();
    let liveCount = 0;
    for (const p of players) perPlayer.set(p.id, 0);
    // entity pass: per-player load, plus deactivation (Z16)
    for (const e of this.active.values()) {
      if (e.state === ST.dead) continue; // corpse timer runs in _update
      liveCount++;
      let dEnt = 1e9, dTarget = 1e9;
      for (const p of players) {
        const d = Math.hypot(e.x - p.x, e.z - p.z);
        if (d < ACT) perPlayer.set(p.id, perPlayer.get(p.id) + 1);
        if (d < dEnt) dEnt = d;
        if (p.id === e.target) dTarget = d;
      }
      // hunting infected are never yanked out from under their target (Z16)
      const hunting = e.A >= INF.awake.alert && dTarget < ACT;
      if (dEnt > INF.stream.deactivate && !hunting) this._despawn(e);
    }

    // slot pass: respawn clocks everywhere, activation only where a player is
    for (const slot of this.slots) {
      if (slot.ent) continue;
      if (slot.respawnT > 0) { slot.respawnT -= dt; continue; }
      let near = 1e9, nearP = null;
      for (const p of players) {
        const dx = slot.hx - p.x, dz = slot.hz - p.z;
        if (dx > ACT || dx < -ACT || dz > ACT || dz < -ACT) continue; // cannot qualify
        const d = Math.hypot(dx, dz);
        if (d < near) { near = d; nearP = p; }
      }
      if (near > ACT) continue;
      if (near < INF.stream.minSpawn) continue; // never materialise in the room (Z15)
      if (liveCount >= INF.stream.capGlobal) continue;
      if (nearP && (perPlayer.get(nearP.id) || 0) >= INF.stream.capPerPlayer) continue;
      this._spawn(slot);
      liveCount++;
      if (nearP) perPlayer.set(nearP.id, (perPlayer.get(nearP.id) || 0) + 1);
    }
  }

  // ------------------------------------------------------------------ senses
  // OCCLUSION — the one question this sim asks about geometry, and the one it
  // used to get wrong in three separate places (sight, the swing, separation).
  //
  // The preferred answer is env.segmentBlocked(), i.e. src/world/query.mjs,
  // which slab-tests the real wall/door/prop/trunk colliders over a y band.
  // Everything below it is the fallback for a host that has not been given
  // that closure yet, built on the env.blocked() point test that has always
  // been there.
  //
  // THE FALLBACK IS FIXED SPACING, NOT A FIXED COUNT. The old test sampled
  // t = 0.25/0.5/0.75/1.0 whatever the length, so at 60 m it looked every
  // 15 m and a wall two metres from the infected's face was never touched —
  // which is why the horde saw through buildings. Spacing is INF.occl.step
  // with a cap, and the walk runs OUTWARD FROM THE INFECTED so the near field
  // is always covered and the common case (a wall right there) exits on the
  // first few samples.
  //
  // The origin itself is sampled, so an infected embedded in geometry is blind
  // and cannot swing, rather than seeing and hitting through the wall it is
  // standing in. That is not a rare case: 126 of the 713 planned home posts
  // land inside the server's own Obstacles model (the audit counted 73 against
  // the browser's thinner wall band), and those were the ones damaging players
  // with no player action at all. It is also exactly what segmentBlocked()
  // answers for a segment starting inside a collider, so the two paths agree.
  // Such an infected cannot move either — tryMove has always refused a step
  // into a wall — so it is inert rather than invulnerable-and-lethal.
  _lineBlocked(ax, az, bx, bz, band) {
    const env = this.env;
    // Non-finite input answers BLOCKED, which is exactly what
    // query.segmentBlocked() answers, so the two paths cannot disagree — and
    // of the two ways to be wrong, refusing a sight line or a swing is visible
    // and harmless while granting one is damage through a wall.
    if (!Number.isFinite(ax) || !Number.isFinite(az)
      || !Number.isFinite(bx) || !Number.isFinite(bz)) return true;
    if (env.segmentBlocked) {
      const [y0, y1] = this._band(ax, az, bx, bz, band);
      return !!env.segmentBlocked(ax, az, bx, bz, y0, y1);
    }
    if (!env.blocked) return false; // no obstacle model at all: nothing occludes
    const dx = bx - ax, dz = bz - az;
    const d = Math.hypot(dx, dz);
    // Spacing first, cap second: `n` is whatever keeps the samples under
    // INF.occl.step apart, and maxSamples only ever catches a line longer than
    // anything that calls this (see the note on it). Inverting the two is how
    // a 120 m sight line came to be sampled every 0.94 m.
    const n = Math.min(INF.occl.maxSamples,
      Math.max(1, Math.ceil(d / INF.occl.step)));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      if (env.blocked(ax + dx * t, az + dz * t)) return true;
    }
    return false;
  }

  // Absolute y interval for an occlusion query. The sim tracks no y at all, so
  // the band can only come from the host: `env.groundY` is OPTIONAL and when
  // it is absent the band is left UNBOUNDED, which is precisely what
  // env.blocked's own baked-in chest band already meant — so an un-updated
  // host sees no behaviour change from the band.
  //
  // Both ends are sampled and the band is their UNION, so a line running up a
  // slope is tested across every height either eye can be at. That errs
  // towards blocked, which is the safe direction: refusing a sight line is
  // visible and harmless, granting one through a wall is the bug being fixed.
  _band(ax, az, bx, bz, band) {
    const g = this.env.groundY;
    if (!g) return [undefined, undefined];
    const ga = g(ax, az), gb = g(bx, bz);
    if (!Number.isFinite(ga) || !Number.isFinite(gb)) return [undefined, undefined];
    return [Math.min(ga, gb) + band[0], Math.max(ga, gb) + band[1]];
  }

  // A MOVEMENT-SIZED step test, for tryMove and for separation.
  //
  // env.blocked is preferred over the segment query here because separation
  // asks once per crowded PAIR rather than once per entity, and a point test
  // is ~20x cheaper. But a point test at the DESTINATION ONLY is only safe
  // while the step is shorter than the thinnest wall band either host
  // publishes (0.6 m on the server's Obstacles), and it is NOT: a separation
  // nudge is at most (1.2 - d) * 0.8 * dt ≈ 0.19 m at 5 Hz, but an
  // INF.speed.chase step is 5.6 * 0.2 = 1.12 m — nearly twice the band — so a
  // chasing infected could step clean through a wall. That is the same defect
  // server/wolves.mjs _advance() was converted to a segment test to fix, and
  // it was fixed in one sim and not the other.
  //
  // So walk the step at the SAME spacing the sight line uses: one test for a
  // separation nudge (unchanged), three for a sprint. The ORIGIN is
  // deliberately not sampled — an entity already standing in geometry (126 of
  // 713 planned home posts land inside the server's own model) has to be able
  // to walk OUT, or it is frozen for ever.
  _stepBlocked(fromX, fromZ, toX, toZ) {
    // A step to nowhere is refused rather than taken: `blocked(NaN, NaN)` is a
    // hash miss, i.e. "clear", which would write the NaN into the entity and
    // lose it for good.
    if (!Number.isFinite(toX) || !Number.isFinite(toZ)) return true;
    if (this.env.blocked) {
      const dx = toX - fromX, dz = toZ - fromZ;
      const n = Math.max(1, Math.ceil(Math.hypot(dx, dz) / INF.occl.step));
      for (let i = 1; i <= n; i++) {
        const t = i / n;
        if (this.env.blocked(fromX + dx * t, fromZ + dz * t)) return true;
      }
      return false;
    }
    return this._lineBlocked(fromX, fromZ, toX, toZ, INF.occl.body);
  }

  // Can this one see that one? Doors are never simulated: both the collision
  // hash and the world query already have the door gaps cut out of the wall
  // segments, so portals come free (Z67).
  //
  // `crawler` picks a band, and no host reads bands yet (INF.occl) — so it
  // changes no answer in the shipped build. It is threaded through anyway so
  // that wiring env.segmentBlocked is a host-side change only.
  los(ax, az, bx, bz, crawler = false) {
    return !this._lineBlocked(ax, az, bx, bz,
      crawler ? INF.occl.crawlEye : INF.occl.eye);
  }

  _sense(e, players, dt) {
    let stim = false;
    e.seen = false;
    const forest = (() => { const b = this.env.biomeAt(e.x, e.z); return b >= 4 && b <= 6; })();
    for (const p of players) {
      const dx = p.x - e.x, dz = p.z - e.z;
      const d = Math.hypot(dx, dz);
      if (d > 130) continue;
      // hearing — sound goes round corners, no LOS needed (Z30)
      const n = p.n || 0;
      if (n > 0 && d < n) {
        e.A += (1 - d / n) * INF.awake.hear * dt;
        e.lkpX = p.x; e.lkpZ = p.z; e.target = p.id;
        stim = true;
      }
      // sight — 60 m × visibility, 45° cone, 8 m 360° bubble (Z25)
      const R = INF.sightBase * (p.v ?? 1) * (forest ? INF.forestMul : 1);
      if (d < R) {
        const inCone = d < INF.nearBubble
          || Math.abs(angWrap(Math.atan2(dx, dz) - e.yaw)) < INF.sightCone;
        if (inCone && this.los(e.x, e.z, p.x, p.z, e.crawler)) {
          e.A += (1 - d / R) * INF.awake.sight * dt;
          e.lkpX = p.x; e.lkpZ = p.z; e.target = p.id;
          e.seen = true;
          stim = true;
        }
      }
    }
    if (!stim) e.A -= INF.awake.decay * dt;
    e.A = Math.max(0, Math.min(INF.awake.max, e.A));
  }

  // one-shot noise ping: never starts a chase, only makes them come looking (Z28)
  ping(x, z, r) {
    // a non-finite radius is not "everywhere": `> NaN` is false for every
    // entity, so an unguarded NaN would wake the entire map (the server clamps
    // this too — this is the second lock on the same door)
    if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(r) || r <= 0) return;
    for (const e of this.active.values()) {
      if (e.state === ST.dead) continue;
      if (Math.hypot(e.x - x, e.z - z) > r) continue;
      e.A = Math.min(INF.awake.max, e.A + INF.awake.ping);
      e.lkpX = x; e.lkpZ = z;
    }
  }

  // horde call — one bad decision in Primorsk becomes a horde, but depth 1
  // stops a single scream cascading across the whole city (Z70)
  _call(e) {
    if (this.time - e.callT < INF.call.cooldown) return;
    e.callT = this.time;
    let n = 0;
    for (const o of this.active.values()) {
      if (o === e || o.state === ST.dead || o.state === ST.chase) continue;
      if (Math.hypot(o.x - e.x, o.z - e.z) > INF.call.radius) continue;
      if (!this.los(e.x, e.z, o.x, o.z, e.crawler)) continue;
      o.A = Math.min(INF.awake.max, o.A + INF.awake.call);
      o.lkpX = e.lkpX; o.lkpZ = e.lkpZ; o.target = e.target;
      o.callT = this.time; // recruits do not re-call
      if (++n >= INF.call.max) break;
    }
  }

  // ---------------------------------------------------------------- movement
  _step(e, tx, tz, speed, dt) {
    const dx = tx - e.x, dz = tz - e.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.35) return true;
    const want = Math.atan2(dx, dz);
    const turn = (e.crawler ? INF.turn * 0.6 : INF.turn) * dt;
    e.yaw += Math.max(-turn, Math.min(turn, angWrap(want - e.yaw)));
    const step = speed * dt;
    const tryMove = (yaw, scale) => {
      const nx = e.x + Math.sin(yaw) * step * scale;
      const nz = e.z + Math.cos(yaw) * step * scale;
      if (this.env.biomeAt(nx, nz) === 0) return false; // no swimming, no wading (Z68)
      if (this._stepBlocked(e.x, e.z, nx, nz)) return false;
      e.x = nx; e.z = nz;
      return true;
    };
    if (tryMove(e.yaw, 1)) { e.stuck = 0; return false; }
    // wall slide: retry along the tangent at 0.7 × speed, either hand
    const side = ((e.id & 1) ? 1 : -1) * (e.stuck > 3 ? -1 : 1);
    if (tryMove(e.yaw + side * Math.PI / 2, 0.7)) { e.stuck++; return false; }
    if (tryMove(e.yaw - side * Math.PI / 2, 0.7)) { e.stuck++; return false; }
    e.yaw += 0.6 * side;
    e.stuck++;
    return false;
  }

  _separate(dt) {
    const cell = new Map();
    for (const e of this.active.values()) {
      if (e.state === ST.dead) continue;
      const k = `${Math.floor(e.x / 4)}_${Math.floor(e.z / 4)}`;
      if (!cell.has(k)) cell.set(k, []);
      cell.get(k).push(e);
    }
    for (const [k, list] of cell) {
      const [cx, cz] = k.split('_').map(Number);
      const nb = [];
      for (let dz = 0; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dz === 0 && dx < 0) continue;
          const o = cell.get(`${cx + dx}_${cz + dz}`);
          if (o) nb.push(...o);
        }
      }
      for (const a of list) {
        for (const b of nb) {
          if (a === b) continue;
          const dx = b.x - a.x, dz = b.z - a.z;
          const d = Math.hypot(dx, dz);
          if (d > 1.2 || d < 1e-4) continue;
          const push = (1.2 - d) * 0.8 * dt / d;
          // Separation used to be the ONE force in the sim that ignored the
          // obstacle model, so a press of bodies against a wall squeezed its
          // neighbours straight through it — no player action needed. Each
          // write is vetoed on its own (the pair is not symmetric: one of them
          // can be pushed into open ground while the other cannot), and the
          // candidate is computed before either is applied so the answer does
          // not depend on which of the two was written first.
          const nax = a.x - dx * push, naz = a.z - dz * push;
          const nbx = b.x + dx * push, nbz = b.z + dz * push;
          if (!this._stepBlocked(a.x, a.z, nax, naz)) { a.x = nax; a.z = naz; }
          if (!this._stepBlocked(b.x, b.z, nbx, nbz)) { b.x = nbx; b.z = nbz; }
        }
      }
    }
  }

  // ----------------------------------------------------------- state machine
  _update(e, players, dt) {
    if (e.state === ST.dead) {
      e.corpseT -= dt;
      if (e.corpseT <= 0) {
        const slot = e.slot;
        this._despawn(e);
        if (slot) {
          slot.respawnT = INF.respawn.cooldown
            + (hash2(slot.si, slot.slot, 41) - 0.5) * 2 * INF.respawn.jitter;
        }
      }
      return;
    }
    if (e.staggerT > 0) {
      e.staggerT -= dt;
      if (e.staggerT <= 0) {
        e.state = e.crawler ? ST.crawl : ST.chase;
        e.A = INF.awake.max;
      }
      return; // no movement, no attack, no cooldown progress (Z40)
    }

    this._sense(e, players, dt);
    const tgt = players.find((p) => p.id === e.target) || null;
    const dT = tgt ? Math.hypot(tgt.x - e.x, tgt.z - e.z) : 1e9;
    const homeD = e.slot ? Math.hypot(e.x - e.slot.hx, e.z - e.slot.hz) : 0;
    const speeds = e.crawler
      ? { wander: INF.speed.crawl, alert: INF.speed.crawl, chase: INF.speed.crawl }
      : INF.speed;

    switch (e.state) {
      case ST.idle:
        e.t -= dt;
        if (e.t <= 0) {
          e.state = ST.wander;
          e.t = 12;
          const a = hash2(e.id, Math.floor(this.time), 51) * Math.PI * 2;
          const r = 4 + hash2(e.id, Math.floor(this.time), 52) * 21;
          e.wx = (e.slot?.hx ?? e.x) + Math.sin(a) * (e.slot?.indoor ? r * 0.15 : r);
          e.wz = (e.slot?.hz ?? e.z) + Math.cos(a) * (e.slot?.indoor ? r * 0.15 : r);
        }
        if (e.A >= INF.awake.alert) this._toAlerted(e);
        break;

      case ST.wander:
        e.t -= dt;
        if (this._step(e, e.wx, e.wz, speeds.wander, dt) || e.t <= 0) {
          e.state = ST.idle;
          e.t = 3 + hash2(e.id, Math.floor(this.time), 53) * 9;
        }
        if (e.A >= INF.awake.alert) this._toAlerted(e);
        break;

      case ST.alerted: {
        if (e.A >= INF.awake.chase && tgt) { this._toChase(e); break; }
        const arrived = Math.hypot(e.lkpX - e.x, e.lkpZ - e.z) < 2;
        if (arrived) {
          e.scanT -= dt;
          // slow yaw sweep while it works out what it heard
          e.yaw += Math.sin(this.time * 1.6 + e.gait * 6) * 1.2 * dt;
          if (e.scanT <= 0) {
            if (e.A < INF.awake.calm) {
              e.state = ST.wander;
              e.t = 12;
              e.wx = e.x; e.wz = e.z;
            } else {
              e.scanT = 4;
            }
          }
        } else {
          this._step(e, e.lkpX, e.lkpZ, speeds.alert, dt);
        }
        break;
      }

      case ST.chase: {
        e.chaseT += dt;
        if (!tgt || tgt.dead) { this._giveUp(e); break; }
        if (dT > INF.chase.loseDist && !e.seen) {
          e.loseT += dt;
          if (e.loseT > INF.chase.loseTime) { this._giveUp(e); break; }
        } else e.loseT = 0;
        if (e.chaseT > INF.chase.maxTime) { this._giveUp(e); break; }
        if (e.slot && homeD > INF.chase.leash) { this._giveUp(e); break; }

        let tx = tgt.x, tz = tgt.z;
        if (dT > 6) {
          // zig-zag lurch: hard to headshot at range, reads as unwell (Z72)
          const off = Math.sin(this.time * 2.2 + e.gait * 12) * 1.6;
          const px = -(tgt.z - e.z) / (dT || 1), pz = (tgt.x - e.x) / (dT || 1);
          tx += px * off; tz += pz * off;
        }
        this._step(e, tx, tz, speeds.chase, dt);
        if (Math.hypot(tgt.x - e.x, tgt.z - e.z) <= (e.crawler ? INF.atk.crawlRange : INF.atk.range)) {
          e.state = ST.attack;
          e.atkPhase = 0; e.atkT = INF.atk.windup; e.swings = 0;
          e.ev = EV.swing;
        }
        break;
      }

      case ST.attack: {
        if (!tgt || tgt.dead) { this._giveUp(e); break; }
        const range = e.crawler ? INF.atk.crawlRange : INF.atk.range;
        // face the victim even while rooted in the swing
        e.yaw += Math.max(-INF.turn * dt, Math.min(INF.turn * dt,
          angWrap(Math.atan2(tgt.x - e.x, tgt.z - e.z) - e.yaw)));
        if (dT > (e.crawler ? range + 0.6 : INF.atk.breakRange)) { e.state = ST.chase; break; }
        e.atkT -= dt;
        if (e.atkT <= 0) {
          // timers ADD rather than assign so the 5 Hz quantisation cannot
          // stretch the cadence (2 swings 0.9 s apart, 2.4 s recovery, i.e.
          // one landed hit per 1.65 s sustained — Z46)
          if (e.atkPhase === 0) {
            // the windup landed — resolve it (Z48)
            if (dT <= range && this._canReach(e, tgt)) this._resolveSwing(e, tgt);
            e.swings++;
            if (e.swings >= 2) {
              e.atkPhase = 2; e.swings = 0;
              e.atkT += Math.max(0.1, INF.atk.recover - INF.atk.windup);
            } else {
              e.atkPhase = 1;
              e.atkT += Math.max(0.05, INF.atk.swingGap - INF.atk.windup);
            }
          } else {
            e.atkPhase = 0;
            e.atkT += INF.atk.windup;
            e.ev = EV.swing; // telegraph: the client plays the claw clip now
          }
        }
        break;
      }

      case ST.crawl:
        // crippled: same brain, one gear, cannot be knocked off its feet (Z42)
        if (e.A >= INF.awake.chase && tgt) {
          this._step(e, tgt.x, tgt.z, INF.speed.crawl, dt);
          if (dT <= INF.atk.crawlRange) {
            e.state = ST.attack;
            e.atkPhase = 0; e.atkT = INF.atk.windup; e.ev = EV.swing;
          }
        } else if (e.A >= INF.awake.alert) {
          this._step(e, e.lkpX, e.lkpZ, INF.speed.crawl, dt);
        } else {
          this._step(e, e.wx, e.wz, INF.speed.crawl, dt);
        }
        break;
      default: break;
    }
  }

  _toAlerted(e) {
    e.state = e.crawler ? ST.crawl : ST.alerted;
    e.scanT = 4;
    e.loseT = 0;
  }

  _toChase(e) {
    if (e.crawler) { e.state = ST.crawl; return; }
    e.state = ST.chase;
    e.chaseT = 0; e.loseT = 0;
    e.ev = EV.scream; // the single most important audio beat in the game (Z36)
    this._call(e);
  }

  _giveUp(e) {
    e.state = e.crawler ? ST.crawl : ST.alerted;
    e.A = Math.min(e.A, INF.awake.chase - 0.05);
    e.scanT = 4;
    e.chaseT = 0; e.loseT = 0;
  }

  // Is the victim actually reachable, or is there a wall in the way? The swing
  // resolver had NO occlusion test at all: the wall band env.blocked reports is
  // 0.6 m against a 2.0 m INF.atk.range, so an infected on the inside of a wall
  // damaged a player standing outside it — and with 126 of 713 home posts
  // embedded in geometry that fired with no player action whatsoever. The arc
  // and the range stay caller policy up in ST.attack; this answers only the
  // geometry half.
  _canReach(e, tgt) {
    return !this._lineBlocked(e.x, e.z, tgt.x, tgt.z,
      e.crawler ? INF.occl.crawlSwing : INF.occl.swing);
  }

  _resolveSwing(e, tgt) {
    const [lo, hi] = INF.atk.dmg;
    this.env.hit({
      k: 'hit', target: tgt.id, kind: 'infected',
      dmg: +(lo + Math.random() * (hi - lo)).toFixed(1),
      bleed: Math.random() < INF.atk.bleed,
      infect: Math.random() < INF.atk.infect,
      dir: +Math.atan2(tgt.x - e.x, tgt.z - e.z).toFixed(2),
      id: e.id,
    });
  }

  // per-target chase cap: surplus aggro holds at the last-known position (Z17.4)
  _capChasers(players) {
    for (const p of players) {
      const chasers = [];
      for (const e of this.active.values()) {
        if (e.target === p.id && (e.state === ST.chase || e.state === ST.attack)) chasers.push(e);
      }
      if (chasers.length <= INF.chase.capPerTarget) continue;
      chasers.sort((a, b) => Math.hypot(a.x - p.x, a.z - p.z) - Math.hypot(b.x - p.x, b.z - p.z));
      for (const e of chasers.slice(INF.chase.capPerTarget)) {
        e.state = ST.alerted;
        e.scanT = 4;
      }
    }
  }

  // ------------------------------------------------------------------ damage
  // zone ∈ head|body|legs, src ∈ melee|gun (Z57–Z60)
  applyDamage(id, dmg, zone = 'body', src = 'melee', fromX = null, fromZ = null) {
    const e = this.active.get(id);
    if (!e || e.state === ST.dead) return 0;
    const mul = zone === 'head' ? INF.headMul : zone === 'legs' ? INF.limbMul : 1;
    const total = dmg * mul;
    e.hp -= total;
    e.A = INF.awake.max; // shot in the back from 200 m → it comes looking (Z58)
    if (fromX != null) { e.lkpX = fromX; e.lkpZ = fromZ; }
    if (zone === 'legs') {
      e.legs += total;
      if (e.legs >= INF.legCripple && !e.crawler) {
        e.crawler = true;
        e.state = ST.crawl;
      }
    }
    if (e.hp <= 0) {
      e.state = ST.dead;
      e.corpseT = INF.respawn.corpse;
      e.ev = EV.death;
      return total;
    }
    e.ev = EV.hurt;
    // stagger, rate-limited so a fast weapon cannot stun-lock (Z41)
    if (!e.crawler && this.time - e.hitT >= INF.stagger.minInterval) {
      let dur = 0;
      if (zone === 'head' || (src === 'melee' && dmg >= 20)) dur = INF.stagger.heavy;
      else if (src === 'melee') dur = Math.random() < INF.stagger.lightChance ? INF.stagger.light : 0;
      else if (Math.random() < INF.stagger.gunChance) dur = INF.stagger.light;
      if (src === 'melee' && dmg >= INF.stagger.knockdownDmg
        && Math.random() < INF.stagger.knockdownChance) dur = INF.stagger.knockdown;
      if (dur > 0) {
        e.hitT = this.time;
        e.staggerT = dur;
        e.state = ST.stagger;
        e.ev = EV.stagger;
      }
    }
    return total;
  }

  // -------------------------------------------------------------------- tick
  tick(dt = INF.tick) {
    this.time += dt;
    const all = this.env.players();
    if (!all.length) return;
    const live = all.filter((p) => !p.dead);
    this._stream(all, dt);
    for (const e of this.active.values()) this._update(e, live, dt);
    this._capChasers(live);
    this._separate(dt);
  }

  // snapshot rows, filtered to 220 m of (px, pz) — exactly what the server is
  // specced to broadcast (Z79). `ev` is consumed here: one-shots fire once.
  snapshot(px = 0, pz = 0, radius = INF.stream.snapshot) {
    const out = [];
    for (const e of this.active.values()) {
      if (Math.hypot(e.x - px, e.z - pz) > radius) continue;
      const state = e.crawler && e.state !== ST.dead && e.state !== ST.stagger
        && e.state !== ST.attack ? ST.crawl : e.state;
      out.push([
        e.id, +e.x.toFixed(1), +e.z.toFixed(1), +e.yaw.toFixed(2),
        state, (e.crawler ? 1 : 0) | ((e.id & 1) << 1), e.ev,
      ]);
      e.ev = EV.none;
    }
    return out;
  }

  counts() {
    const by = [0, 0, 0, 0, 0, 0, 0, 0];
    for (const e of this.active.values()) by[e.state]++;
    return { active: this.active.size, planned: this.planned, byState: by };
  }
}
